/**
 * @file PendingRequests.jsx
 * @description Manages display and handling of pending trading requests
 *
 * @section Responsibilities
 * - Display pending requests
 * - Handle request management
 * - Process request data
 * - Manage request state
 * - Handle request errors
 * - Provide request controls
 *
 * @section Dependencies
 * - RequestService: Request management
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Request API: Request operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: Request controls
 *
 * @section State Management
 * - requestState: Request status
 * - dataState: Data status
 * - controlState: Control status
 * - errorState: Error handling
 *
 * @section Events
 * - onRequestUpdate: Request updates
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Array} requests - Pending requests
 * @prop {Function} onUpdate - Update callback
 * @prop {Object} initialData - Initial request data
 * @prop {Boolean} autoUpdate - Controls auto-updates
 */

import React, { useState, useRef } from "react";
import Widget from "../Widget";
import Request from "../../../../utils/fetchUtils";
import Swal from "sweetalert2";
import DgModal from "../../../../components/DgModal";
import DgButton from "../../../../components/DgButton";

function PendingRequests({ strategy, fetch }) {
  const { customersRequests = [] } = strategy;
  const [modalShow, setModalShow] = useState(false);
  const [toBeRejected, setToBeRejected] = useState(false);
  const [warning, setWarning] = useState(false);

  const formRef = useRef(null);

  const resetForm = () => {
    formRef.current.reset();
    setWarning("");
    handleModal(false);
  };

  const handleModal = (isOpen, cr) => {
    setModalShow(isOpen);
    if (cr) {
      setToBeRejected(cr);
    }
  };

  const handleApproval = async (isApproved, customerKey) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You really want to ${
        isApproved ? "Approve" : "Reject"
      } this client allocation request?`,
      icon: "question",
      showCancelButton: true,
    }).then(async ({ isConfirmed }) => {
      handleRequest(isApproved, isConfirmed, customerKey);
    });
  };

  const handleRequest = async (
    isApproved,
    isConfirmed,
    customerKey,
    emailBody
  ) => {
    if (isConfirmed) {
      const res = await Request(
        `/trading/strategy/${strategy.strategyId}/${
          isApproved ? "accept-client" : "reject-client"
        }`,
        {
          customerKey,
        },
        "PATCH"
      );
      if (res.ok) {
        const title = isApproved ? "User accepted" : "User rejected";
        Swal.fire({
          title,
          text: "An alert will be sent to the advisor",
          icon: "success",
        }).then(() => {
          fetch();
        });
      } else {
        Swal.fire({
          title: "Oops..",
          text: res.message,
          icon: "error",
        });
      }
    }
  };

  const handleReject = (userSk) => {
    const form = formRef.current;
    const selectedOption = form.querySelector(
      'input[name="reject-reason"]:checked'
    );
    const additionalMessage = form
      .querySelector('textarea[name="message"]')
      .value.trim();

    if (!selectedOption) {
      setWarning("Please select a reason for rejection.");
      return;
    }

    let submissionMessage = `Reason for rejection: ${selectedOption.value}.`;
    if (additionalMessage) {
      submissionMessage += ` Additional details: ${additionalMessage}.`;
    }

    resetForm();
    handleRequest(false, true, userSk, submissionMessage);
  };

  return (
    <Widget name={"pending-requests"} width={"full-width"}>
      <div className="heading">
        <h2 className="left-title">Pending requests</h2>
      </div>
      <table className="basic-table">
        <thead>
          <tr>
            <th>Client</th>
            {/* <th>Risk score</th> */}
            {/* <th>Investing amount</th> */}
            <th>Percentage</th>
            {/* <th>Advisor</th> */}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {customersRequests.length > 0 ? (
            customersRequests.map((cr, index) => (
              <tr key={`cReq-${index}`}>
                <td className="name first">
                  <div className="user">
                    <div>
                      <span className="name">{cr.userName}</span>
                    </div>
                  </div>
                </td>
                {/* <td className="risk-score">
                  <p className="score-text">{cr.percentage}</p>
                </td> */}
                {/* <td className="inv-amount">
                  <p className="score-text">$4500</p>
                </td> */}
                <td className="percentage">
                  <p className="score-text">{cr.percentage}%</p>
                </td>
                {/* <td className="advisor">
                  <p className="score-text">John Doe</p>
                </td> */}
                <td className="actions">
                  <div className="actions-wrapper">
                    <img
                      src="/assets/icons/check.svg"
                      className="accept"
                      alt=""
                      onClick={() => {
                        handleApproval(true, cr.userSk);
                      }}
                    />
                    <img
                      src="/assets/icons/cross.svg"
                      className="deny"
                      alt=""
                      onClick={() => {
                        handleModal(true, cr);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5}>No pending requests</td>
            </tr>
          )}
        </tbody>
      </table>
      <DgModal
        modalState={modalShow}
        handleModalState={() => {
          handleModal(!modalShow);
        }}
      >
        <div className="modal">
          <h1 className="title">Are you sure?</h1>
          <div className="description">
            Tell us why you will be rejecting {toBeRejected.userName} from the
            strategy
          </div>
          <div className="description">
            Client's investing percentage:
            {/* $4500 | Client's risk score:{" "} */}
            {toBeRejected.percentage}
          </div>
          <form className="rejectForm" ref={formRef}>
            <div className="form-check">
              <div className="radio-option">
                <input type="radio" name="reject-reason" value="low-score" />
                <label>Risk score is too low</label>
              </div>
            </div>
            <div className="form-check">
              <div className="radio-option">
                <input type="radio" name="reject-reason" value="low-amount" />
                <label>Investing amount is too low</label>
              </div>
            </div>
            <div className="form-check">
              <div className="radio-option">
                <input type="radio" name="reject-reason" value="high-score" />
                <label>Risk score is too high</label>
              </div>
            </div>
            <div className="form-check">
              <div className="radio-option">
                <input type="radio" name="reject-reason" value="high-amount" />
                <label>Investing amount is too high</label>
              </div>
            </div>
            <div className="form-check">
              <div className="radio-option">
                <input type="radio" name="reject-reason" value="other" />
                <label>Other</label>
              </div>
            </div>
            <div className="message">
              <label className="description">
                Add details for the advisor: (Optional)
              </label>
              <br />
              <textarea name="message"></textarea>
            </div>
            {warning && <p className="warning">{warning}</p>}
            <div className="flex">
              <DgButton
                category={"primary"}
                size={"medium"}
                text={"Reject user"}
                action={(e) => {
                  e.preventDefault();
                  handleReject(toBeRejected.userSk);
                }}
              ></DgButton>
              <DgButton
                category={"secondary"}
                size={"medium"}
                text={"Cancel"}
                action={(e) => {
                  e.preventDefault();
                  resetForm();
                }}
              ></DgButton>
            </div>
          </form>
        </div>
      </DgModal>
    </Widget>
  );
}

export default PendingRequests;
